'use client';

import { FormControl, FormField, FormItem, FormMessage } from 'shared/ui/form';
import FormControlWithLabel from 'shared/ui/form-control-with-label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shared/ui/select';
import { ConfigFields } from 'features/change-config/ui/config-fields';
import { configurations } from '@/enteties/config/api';
import { useFormContext } from 'react-hook-form';
import type { ToolchainData } from 'shared/types/configuration-service';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { setDefaultValues } from 'features/change-config/util';
import type { VariantProps } from 'class-variance-authority';
import { configVariants, fieldVariants, SkeletonConfig } from 'features/change-config/ui/skeleton-config';
import mergeClasses from 'shared/lib/utils';
import { useQueryWithOnError } from 'shared/hooks/query/useQueryWithOnError';
import * as m from '@paraglide/messages';
import { useValidateConfig } from '../hooks/useValidateConfig';
interface ConfigProps extends VariantProps<typeof configVariants>, VariantProps<typeof fieldVariants> {
  slug: string;
}
export const Config = ({
  variant,
  fields,
  slug
}: ConfigProps) => {
  const {
    data: allConfigs,
    isLoading,
    isSuccess
  } = useQueryWithOnError({
    ...configurations.getAllConfigurationsQueryOptions(),
    staleTime: Infinity
  });
  const [environments, setEnvironments] = useState<string[]>([]);
  useEffect(() => {
    if (isSuccess) setEnvironments(Object.keys(allConfigs));
  }, [isSuccess]);
  const {
    suggestions,
    mutateValidate,
    validateSuccess
  } = useValidateConfig(slug, variant === 'model');
  const form = useFormContext();
  const [environment, toolchainName] = form.watch(['meta.environment', 'meta.toolchain.name']);
  useEffect(() => {
    if (environment && isSuccess && allConfigs?.[environment] === undefined) {
      form.reset({
        meta: {
          environment: undefined,
          toolchain: {
            name: undefined
          }
        }
      }, {
        keepDefaultValues: false,
        keepDirty: false,
        keepErrors: false,
        keepIsSubmitted: false,
        keepTouched: false,
        keepIsValid: false,
        keepSubmitCount: false
      });
    }
  }, [isSuccess, environment, allConfigs]);
  const toolChainDataInit = useMemo(() => allConfigs?.[environment]?.[toolchainName], [allConfigs, environment, toolchainName]);
  const handleEnvironmentChange = useCallback(<T extends string,>(event: T, onChange: (event: T) => void) => {
    if (!event) return;
    onChange(event);
  }, []);
  const handleToolChainChange = useCallback((event: string, onChange: (event: string) => void) => {
    if (!event) return;
    const toolchainData: ToolchainData = allConfigs?.[environment][event];
    const formDefaults = setDefaultValues(toolchainData, environment, event);
    form.reset(formDefaults);
    mutateValidate(formDefaults);
    return onChange(event);
  }, [allConfigs, environment, form, mutateValidate]);
  if (isLoading) return <SkeletonConfig />;
  return <div className={mergeClasses(configVariants({
    variant
  }))} data-sentry-component="Config" data-sentry-source-file="config.tsx">
      <FormField control={form.control} name="meta.environment" render={({
      field
    }) => <FormItem className="">
            <FormControlWithLabel text={m.tab_config_field_environment_label()}>
              <Select onValueChange={event => handleEnvironmentChange(event, field.onChange)} value={environment ?? field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={m.select_default()} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {environments?.map(key => <SelectItem key={key} value={key}>
                      {key}
                    </SelectItem>)}
                </SelectContent>
              </Select>
            </FormControlWithLabel>
            <FormMessage />
          </FormItem>} data-sentry-element="FormField" data-sentry-source-file="config.tsx" />

      <FormField control={form.control} name="meta.toolchain.name" render={({
      field
    }) => <FormItem className="">
            <FormControlWithLabel text={m.tab_config_field_toolchain_label()}>
              <Select onValueChange={event => handleToolChainChange(event, field.onChange)} value={toolchainName ?? field.value} disabled={environment === undefined}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={m.select_default()} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {environment && allConfigs && allConfigs[environment] && <>
                      {Object?.keys(allConfigs?.[environment]).map(toolChain => <SelectItem key={toolChain} value={toolChain}>
                            {toolChain}
                          </SelectItem>)}
                    </>}
                </SelectContent>
              </Select>
            </FormControlWithLabel>
            <FormMessage />
          </FormItem>} data-sentry-element="FormField" data-sentry-source-file="config.tsx" />

      <div className={mergeClasses(fieldVariants({
      fields
    }))}>
        {toolchainName ? <ConfigFields toolchainDataInit={toolChainDataInit} environment={environment} toolchainName={toolchainName} suggestions={suggestions} mutateValidate={mutateValidate} /> : null}
      </div>
    </div>;
};